"use client";

// ============================================================
// CLUBHUB – Gruppe anlegen oder bearbeiten
// ============================================================
// Ein Dialog fuer beides. Name und Art zuerst (mehr braucht eine
// Gruppe nicht), darunter, was sie im Verein verortet: wo sie im
// Baum haengt, ihre Vereins-Mailadresse, die Kennfarbe und — bei
// Abteilungen und Mannschaften — die Sportart fuer die Meldung an
// den Landessportverband.
// ============================================================
import { useMemo, useState } from "react";
import { Lock } from "lucide-react";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogRow,
  DialogSection,
  DialogSubmit,
  DialogTitle,
  FieldLabel,
  Input,
  Select,
  Textarea,
  toast,
} from "@/components/ui";
import { GroupKind } from "@/generated/prisma/enums";
import { GROUP_KIND } from "@/lib/labels";
import type { ActionResult } from "@/lib/action-result";
import type { GruppeEingabe, GruppeView } from "@/lib/gruppen/views";
import { aliasFehler, aliasVorschlag, nachfahren, pfadText } from "@/lib/gruppen/logik";
import { cn } from "@/lib/utils";
import { useGruppenBestand } from "./gruppen-bestand";
import { Farbwahl } from "./farbwahl";

const SPORTARTEN = [
  "Fußball · BFV",
  "Futsal · BFV",
  "Turnen · BTV",
  "Leichtathletik · BLV",
  "Tischtennis · BTTV",
  "Tennis · BTV",
  "Volleyball · BVV",
  "Gymnastik und Fitness · BTV",
];

const MIT_SPORT = new Set<GroupKind>(["TEAM", "DEPARTMENT"]);

type Fehler = Partial<Record<"name" | "alias" | "parentId", string>>;

export function GruppeDialog({
  offen,
  onClose,
  gruppe,
  vorgabeEltern,
  onGespeichert,
}: {
  offen: boolean;
  onClose: () => void;
  /** Gesetzt: bearbeiten. Leer: neu anlegen. */
  gruppe?: GruppeView;
  /** Neu als Untergruppe von … */
  vorgabeEltern?: string | null;
  onGespeichert?: (g: GruppeView) => void;
}) {
  return (
    <Dialog open={offen} onClose={onClose}>
      {/* Neu aufgebaut bei jedem Oeffnen: die Felder starten mit den Werten der Gruppe. */}
      {offen && <Formular gruppe={gruppe} vorgabeEltern={vorgabeEltern} onGespeichert={onGespeichert} />}
    </Dialog>
  );
}

function Formular({
  gruppe,
  vorgabeEltern,
  onGespeichert,
}: {
  gruppe?: GruppeView;
  vorgabeEltern?: string | null;
  onGespeichert?: (g: GruppeView) => void;
}) {
  const { gruppen, maildomain, quelle, gruppeAnlegen, gruppeAendern } = useGruppenBestand();
  const eltern0 = gruppe?.parentId ?? vorgabeEltern ?? null;
  const elternGruppe = gruppen.find((g) => g.id === eltern0);

  const [name, setName] = useState(gruppe?.name ?? "");
  const [kind, setKind] = useState<GroupKind>(gruppe?.kind ?? "TEAM");
  const [parentId, setParentId] = useState<string>(eltern0 ?? "");
  const [alias, setAlias] = useState(gruppe?.emailAlias?.split("@")[0] ?? "");
  // Farbe und Sportart der Elterngruppe nur als Vorschlag beim Anlegen —
  // beim Bearbeiten gilt, was die Gruppe hat, auch wenn es leer ist.
  const [color, setColor] = useState<string | null>(gruppe ? gruppe.color : (elternGruppe?.color ?? null));
  const [sportCode, setSportCode] = useState(gruppe ? (gruppe.sportCode ?? "") : (elternGruppe?.sportCode ?? ""));
  const [sportListe, setSportListe] = useState(() =>
    gruppe?.sportCode && !SPORTARTEN.includes(gruppe.sportCode) ? [...SPORTARTEN, gruppe.sportCode] : SPORTARTEN,
  );
  const [description, setDescription] = useState(gruppe?.description ?? "");
  const [fehler, setFehler] = useState<Fehler>({});

  // Wohin die Gruppe haengen darf: nie unter sich selbst oder eine eigene Untergruppe.
  const elternOptionen = useMemo(() => {
    const gesperrt = gruppe ? new Set([gruppe.id, ...nachfahren(gruppen, gruppe.id)]) : new Set<string>();
    return [
      { value: "", label: "Keine — oberste Ebene" },
      ...gruppen
        .filter((g) => !g.isArchived && !gesperrt.has(g.id))
        .map((g) => ({ value: g.id, label: pfadText(gruppen, g.id), hint: GROUP_KIND[g.kind] }))
        .sort((a, b) => a.label.localeCompare(b.label, "de")),
    ];
  }, [gruppen, gruppe]);

  const vorschlag = aliasVorschlag(name);

  async function speichern(): Promise<ActionResult<{ gruppe: GruppeView }>> {
    const n = name.trim();
    if (n.length < 2) return { error: "Bitte einen Namen mit mindestens zwei Zeichen.", field: "name" };
    const doppelt = gruppen.find(
      (g) => g.id !== gruppe?.id && !g.isArchived && g.name.toLowerCase() === n.toLowerCase() && (g.parentId ?? "") === parentId,
    );
    if (doppelt) return { error: `„${doppelt.name}“ gibt es an dieser Stelle schon.`, field: "name" };
    const a = alias.trim().toLowerCase();
    const af = aliasFehler(a, maildomain, gruppen, gruppe?.id);
    if (af) return { error: af, field: "alias" };

    const eingabe: GruppeEingabe = {
      name: n,
      kind,
      parentId: parentId || null,
      emailAlias: a ? `${a}@${maildomain}` : null,
      color,
      sportCode: MIT_SPORT.has(kind) && sportCode ? sportCode : null,
      description: description.trim() || null,
    };
    return gruppe ? gruppeAendern(gruppe.id, eingabe) : gruppeAnlegen(eingabe);
  }

  return (
    <DialogForm
      action={speichern}
      refresh={false}
      success={(r) =>
        "gruppe" in r
          ? gruppe
            ? `„${r.gruppe.name}“ gespeichert`
            : quelle === "muster"
              ? { title: `„${r.gruppe.name}“ angelegt`, description: "Im Musterbestand nur bis zum Neuladen — gespeichert wird nach der Anmeldung." }
              : `„${r.gruppe.name}“ angelegt`
          : null
      }
      onError={(meldung, r) => {
        const feld = r && "field" in r ? (r.field as keyof Fehler) : undefined;
        if (feld) setFehler({ [feld]: meldung });
        else toast.error(meldung);
      }}
      onSuccess={(r) => {
        if ("gruppe" in r) onGespeichert?.(r.gruppe);
      }}
    >
      <DialogHeader>
        <DialogTitle>{gruppe ? "Gruppe bearbeiten" : vorgabeEltern ? "Untergruppe anlegen" : "Neue Gruppe"}</DialogTitle>
        <DialogDescription>
          {gruppe
            ? pfadText(gruppen, gruppe.id)
            : elternGruppe
              ? `Unter „${pfadText(gruppen, elternGruppe.id)}“`
              : "Mannschaft, Abteilung, Vorstand, Ausschuss — alles ist eine Gruppe."}
        </DialogDescription>
      </DialogHeader>

      <DialogContent>
        <DialogRow template="1fr 11rem">
          <Input
            name="name"
            label="Name"
            required
            autoComplete="off"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFehler((f) => ({ ...f, name: undefined }));
            }}
            placeholder="z. B. U11, Damen, Kassenprüfung"
            error={fehler.name}
          />
          <Select
            name="kind"
            label="Art"
            value={kind}
            onChange={(v) => setKind(v as GroupKind)}
            options={Object.values(GroupKind).map((k) => ({ value: k, label: GROUP_KIND[k] }))}
          />
        </DialogRow>

        <Select
          name="parentId"
          label="Übergeordnete Gruppe"
          value={parentId}
          onChange={setParentId}
          options={elternOptionen}
          searchable
          hint="Mannschaften hängen unter ihrer Abteilung. Die Abteilung zählt ihre Mitglieder mit."
          error={fehler.parentId}
        />

        <div className="space-y-1.5">
          <FieldLabel htmlFor="gruppe-alias">Vereins-Mailadresse</FieldLabel>
          <div
            className={cn(
              "bg-bg-primary flex h-10 items-center rounded-lg border pr-3",
              "focus-within:border-accent/40 focus-within:ring-accent/30 duration-fast transition-[box-shadow,border-color] focus-within:ring-2",
              fehler.alias ? "border-danger" : "border-border hover:border-border-strong",
            )}
          >
            <input
              id="gruppe-alias"
              name="alias"
              value={alias}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                setAlias(e.target.value.toLowerCase().replace(/\s/g, ""));
                setFehler((f) => ({ ...f, alias: undefined }));
              }}
              placeholder={vorschlag || "jugend"}
              aria-invalid={fehler.alias ? "true" : undefined}
              aria-describedby="gruppe-alias-hinweis"
              className="text-body-sm text-text-primary placeholder:text-text-tertiary h-full min-w-0 flex-1 bg-transparent px-3 outline-none"
            />
            <span className="text-body-sm text-text-tertiary shrink-0 truncate">@{maildomain}</span>
          </div>
          <p id="gruppe-alias-hinweis" className={cn("text-caption", fehler.alias ? "text-danger" : "text-text-tertiary")} role={fehler.alias ? "alert" : undefined}>
            {fehler.alias ??
              (alias
                ? "Mails an diese Adresse gehen an alle aktuellen Mitglieder der Gruppe, in Blindkopie."
                : vorschlag
                  ? `Leer lassen, wenn die Gruppe keine Adresse braucht — oder z. B. „${vorschlag}“.`
                  : "Leer lassen, wenn die Gruppe keine Adresse braucht.")}
          </p>
        </div>

        <DialogSection>
          <div className="space-y-2">
            <FieldLabel>Kennfarbe</FieldLabel>
            <Farbwahl wert={color} onChange={setColor} name="color" />
            <p className="text-caption text-text-tertiary">Erscheint im Kalender, auf der Pinnwand und in Listen.</p>
          </div>

          {MIT_SPORT.has(kind) && (
            <Select
              name="sportCode"
              label="Sportart und Fachverband"
              value={sportCode}
              onChange={setSportCode}
              options={[{ value: "", label: "Keine Angabe" }, ...sportListe.map((s) => ({ value: s, label: s }))]}
              creatable={{
                onCreate: (getippt) => {
                  setSportListe((l) => [...l, getippt]);
                  setSportCode(getippt);
                },
                label: (getippt) => `„${getippt}“ übernehmen`,
              }}
              hint="Für die jährliche Bestandserhebung beim Landessportverband — gemeldet wird je Sportart."
            />
          )}

          <Textarea
            name="description"
            label="Beschreibung"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Jahrgänge, Spielklasse, Zweck — ein, zwei Sätze."
          />
        </DialogSection>

        <DialogNotice icon={<Lock className="text-text-tertiary h-4 w-4" />}>
          Mitglieder dieser Gruppe sehen einander nur mit Namen. Kontaktdaten sieht nur, wer das Recht dazu hat — eine Funktion in der Gruppe gibt keine Rechte.
        </DialogNotice>
      </DialogContent>

      <DialogFooter>
        <DialogCancel />
        <DialogSubmit>{gruppe ? "Speichern" : "Gruppe anlegen"}</DialogSubmit>
      </DialogFooter>
    </DialogForm>
  );
}
